import { useQueryClient } from '@tanstack/react-query';

import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import type { DiscountsTableRow } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { queries } from '@config/server/queryKeys';

import { getDiscount } from '../api/useGetDiscount';
import { DiscountDetailsTab, discountDrawerModeAtom, NEW_DISCOUNT_ID } from '../atoms/discountDetailsDrawerAtoms';
import { discountFormAtom } from '../atoms/discountFormAtoms';
import { mapResponseToFormState } from '../utils/mapResponseToFormState';

import { useDiscountNavigation } from './useDiscountNavigation';

/**
 * Hook that provides a function to duplicate a discount.
 *
 * When called, it:
 * 1. Fetches the full discount data from the API (or cache)
 * 2. Maps the response to form state with code/title cleared
 * 3. Sets drawer mode to 'duplicate'
 * 4. Navigates to the new-discount sentinel so the drawer opens in duplicate mode
 */
export const useDuplicateDiscount = (): ((row: DiscountsTableRow) => void) => {
	const setFormState = useSetAtom(discountFormAtom);
	const setDrawerMode = useSetAtom(discountDrawerModeAtom);
	const { openDiscountDetails } = useDiscountNavigation();
	const queryClient = useQueryClient();
	const servicesProviderId = useCurrentServicesProviderId();

	return useCallback(
		(row: DiscountsTableRow) => {
			const discountId = row.id;

			// Fetch full discount data so all fields are populated
			queryClient
				.fetchQuery({
					queryKey: queries.discounts.details(servicesProviderId, discountId).queryKey,
					queryFn: () => getDiscount(servicesProviderId, discountId),
					staleTime: 5 * 60 * 1000,
				})
				.then((response) => {
					const formData = mapResponseToFormState(response);
					setFormState({ ...formData, code: '', title: '' });
					setDrawerMode('duplicate');
					openDiscountDetails(NEW_DISCOUNT_ID, DiscountDetailsTab.OVERVIEW);
				});
		},
		[setFormState, setDrawerMode, openDiscountDetails, queryClient, servicesProviderId],
	);
};
